import { useState } from 'react';
import { Button, Input, InputNumber, Select, Spin, Switch, Tag, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined, UndoOutlined } from '@ant-design/icons';
import type { Workspace } from '@core/entities/Workspace';
import type { RequestSettings } from '@core/entities/RequestSettings';
import {
  REQUEST_SETTINGS_FIELDS,
  type FieldKey,
} from '@presentation/constants/requestSettingsFields';
import { useRequestSettingsDefaults } from '@presentation/hooks/useRequestSettings';
import { useUpdateWorkspace } from '@presentation/hooks/useWorkspaces';
import { useAppMessage } from '@presentation/hooks/useAppMessage';

interface WorkspaceDefaultSettingsSectionProps {
  workspace: Workspace;
}

/**
 * The middle layer of the settings cascade (see RequestSettingsResolver):
 * defaults every request in this workspace inherits unless it sets its own
 * override. Reuses REQUEST_SETTINGS_FIELDS from the request-level Settings
 * tab so the two stay in lockstep, with simpler two-layer scoping (custom
 * vs. application default) since there's no request layer above this one.
 */
export const WorkspaceDefaultSettingsSection = ({
  workspace,
}: WorkspaceDefaultSettingsSectionProps) => {
  const message = useAppMessage();
  const { data: defaults, isLoading } = useRequestSettingsDefaults();
  const updateWorkspace = useUpdateWorkspace(workspace.id);
  const [draft, setDraft] = useState<RequestSettings>(workspace.settings);

  const setField = (key: FieldKey, value: unknown) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const resetField = (key: FieldKey) =>
    setDraft((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });

  const handleSave = () => {
    updateWorkspace.mutate(
      { settings: draft },
      { onSuccess: () => void message.success('Default request settings saved.') },
    );
  };

  if (isLoading || !defaults) {
    return (
      <div className="flex justify-center py-6">
        <Spin size="small" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Typography.Text type="secondary">
        Defaults every request in this workspace inherits unless it sets its own override in its
        Settings tab.
      </Typography.Text>

      <div className="flex flex-col divide-y divide-black/5 dark:divide-white/10">
        {REQUEST_SETTINGS_FIELDS.map((field) => {
          const value = draft[field.key];
          const appDefault = defaults.appDefaults[field.key];
          const isCustom = value !== undefined;

          return (
            <div key={field.key} className="flex items-center gap-3 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm">{field.label}</span>
                  <Tooltip title={field.description}>
                    <QuestionCircleOutlined className="text-xs text-gray-400" />
                  </Tooltip>
                </div>
                <Tag color={isCustom ? 'purple' : 'default'} className="mt-1">
                  {isCustom ? 'Workspace default' : 'Application default'}
                </Tag>
              </div>

              {field.control.type === 'switch' && (
                <Switch
                  checked={(value as boolean | undefined) ?? (appDefault as boolean)}
                  onChange={(checked) => setField(field.key, checked)}
                />
              )}
              {field.control.type === 'select' && (
                <Select
                  size="small"
                  className="w-40"
                  value={(value as string | undefined) ?? (appDefault as string)}
                  options={field.control.options}
                  onChange={(next) => setField(field.key, next)}
                />
              )}
              {field.control.type === 'number' && (
                <InputNumber
                  size="small"
                  min={field.control.min}
                  max={field.control.max}
                  value={(value as number | undefined) ?? (appDefault as number)}
                  onChange={(next) => setField(field.key, next ?? undefined)}
                />
              )}
              {field.control.type === 'text' && (
                <Input
                  size="small"
                  className="!w-64"
                  placeholder={field.control.placeholder}
                  value={(value as string | null | undefined) ?? ''}
                  onChange={(event) => setField(field.key, event.target.value || null)}
                />
              )}

              <Tooltip title="Reset to application default">
                <Button
                  size="small"
                  type="text"
                  icon={<UndoOutlined />}
                  disabled={!isCustom}
                  aria-label={`Reset ${field.label}`}
                  onClick={() => resetField(field.key)}
                />
              </Tooltip>
            </div>
          );
        })}
      </div>

      <Button type="primary" size="small" loading={updateWorkspace.isPending} onClick={handleSave}>
        Save defaults
      </Button>
    </div>
  );
};
